/**
 * Strip and parse a leading YAML front-matter block (sdx#1109).
 *
 * Markdown documents in the repos carry a `---` front-matter header (`title`,
 * `tags`, …). The reader renders raw file content, so without stripping, marked
 * turns `tags: [x]` followed by the closing `---` into a setext `<h2>` — the
 * "front-matter junk" at the top of every doc. This removes the block before
 * rendering and returns its keys.
 *
 * Front-matter is, precisely: a first line that is exactly `---`, then lines of
 * YAML, then a closing line that is exactly `---` (or `...`). Anything else —
 * no leading fence, or a leading `---` with no closing fence (a genuine
 * thematic break / an unterminated block) — is NOT front-matter, and the body
 * is returned byte-for-byte unchanged so ordinary documents render exactly as
 * before.
 *
 * The parser is a deliberately small YAML subset (scalars, inline `[a, b]` and
 * block `- a` arrays) — enough for the keys the UI reads. The backend's
 * `doc_index` remains the authoritative front-matter parser; this only serves
 * live rendering.
 */

export interface FrontMatter {
  data: Record<string, unknown>;
  /** Content with the front-matter block removed (byte-identical when none). */
  body: string;
}

const FENCE = /^(---|\.\.\.)$/;

export function parseFrontMatter(content: string): FrontMatter {
  // Split on '\n' only, so a '\r' stays on each line and CRLF is preserved when
  // the body is rejoined — the absent/unterminated paths return `content`
  // itself, so those are byte-identical regardless.
  const lines = content.split('\n');
  if (lines[0].trim() !== '---') return { data: {}, body: content };

  let end = -1;
  for (let i = 1; i < lines.length; i++) {
    if (FENCE.test(lines[i].trim())) { end = i; break; }
  }
  if (end === -1) return { data: {}, body: content }; // unterminated → leave as-is

  const data = parseYamlSubset(lines.slice(1, end));
  return { data, body: lines.slice(end + 1).join('\n') };
}

function dequote(s: string): string {
  if (s.length >= 2 && ((s[0] === '"' && s.endsWith('"')) || (s[0] === "'" && s.endsWith("'")))) {
    return s.slice(1, -1);
  }
  return s;
}

function coerce(s: string): unknown {
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s !== '' && !Number.isNaN(Number(s))) return Number(s);
  return s;
}

function parseYamlSubset(lines: string[]): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === '' || trimmed.startsWith('#')) continue;
    const m = line.match(/^([A-Za-z0-9_-]+):[ \t]*(.*?)[ \t]*\r?$/);
    if (!m) continue; // not a top-level key (block-array items are consumed below)
    const key = m[1];
    const rest = m[2];

    if (rest === '') {
      // A block array on the following indented `- item` lines, else an empty
      // scalar. `#` comments among the items are skipped.
      const items: unknown[] = [];
      while (i + 1 < lines.length && /^[ \t]+-[ \t]*/.test(lines[i + 1])) {
        const item = lines[++i].replace(/^[ \t]+-[ \t]*/, '').replace(/\r$/, '').trim();
        if (item !== '' && !item.startsWith('#')) items.push(coerce(dequote(item)));
      }
      data[key] = items.length ? items : '';
    } else if (rest.startsWith('[') && rest.endsWith(']')) {
      data[key] = rest.slice(1, -1).split(',').map((s) => coerce(dequote(s.trim()))).filter((v) => v !== '');
    } else {
      data[key] = coerce(dequote(rest));
    }
  }
  return data;
}
